// Money rules that BOTH ends must agree on, shared so they cannot drift.
//
// The API and the web app each have their own money module — `payments/ledger.ts`
// for validation and `lib/money.ts` for parsing and formatting — and that is
// right: they answer different questions. What belongs HERE is the narrow set of
// rules where a disagreement between the two is itself the bug.

// Cents, rounded AWAY FROM ZERO on both sides. Money is compared and summed in
// integer cents because 0.1 + 0.2 is 0.30000000000000004, and these figures are
// mirrored into a numeric(10,2) column that would then disagree with its ledger.
//
// NOT `Math.round`, which breaks ties toward positive infinity and so is
// asymmetric about zero (PR #159 review). `Math.round(-0.5)` is `-0`, so
// `-0.005` rounded to nothing and read as an instruction to CLEAR the payment,
// while `+0.005` rounded to one cent and did not. Postgres stores both as ±0.01
// — verified against the dev branch — so a negative adjustment of half a cent,
// which is an ordinary supported entry here, was wiping the whole row.
export function cents(amount: number): number {
  const scaled = amount * 100;
  const rounded = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
  // Never NEGATIVE ZERO. `-Math.round(0.4)` is `-0`, which compares equal to 0
  // and so behaves correctly here — but it survives into sums and `Object.is`
  // comparisons as a different value, and a figure meaning "nothing" should have
  // exactly one representation.
  return rounded === 0 ? 0 : rounded;
}

/**
 * Does this amount mean "clear this payment"?
 *
 * ONE RULE, SHARED, because the two ends ask it from opposite sides and a
 * disagreement is silent money (PR #159 review, both reviewers). The server
 * rounds to cents before deciding — a figure under half a cent stores as zero,
 * so it must be treated as zero — while the client originally compared against
 * an exact `0`. Typing `0.004` therefore showed "recorded a correction" in the
 * modal and voided the whole payment on the server: the one line of history that
 * makes a void visible rather than silent was, for that input, invisible to the
 * person who caused it.
 *
 * JUNK IS NOT A CLEAR. `Number(null)`, `Number("")` and `Number([])` are all 0,
 * and reading any of them as "remove this payment" would destroy money on a
 * malformed body. Only a real number, or a string that is one, can mean it;
 * everything else falls through to the caller's own validation and its 400.
 */
export function clearsPayment(amount: unknown): boolean {
  const n =
    typeof amount === "number"
      ? amount
      : typeof amount === "string" && amount.trim() !== ""
        ? Number(amount)
        : Number.NaN;
  return Number.isFinite(n) && cents(n) === 0;
}
